import { piecePath, type BareAudioMime } from './audioFormat';
import { HuddleRpcError, type ChunkRecord } from './huddleApi';

/**
 * The upload queue for recorded pieces (h1h2-plan.md 2.4 and 2.6, R-E, N2).
 *
 * One upload at a time, in seq order. File first, row second, so a row never
 * points at a missing file. Every answer is sorted into one of three kinds:
 *
 *   ok       the upload landed, or the store says the object already exists
 *            (a retry after a lost reply). Dev4 B5: matched on the error
 *            MESSAGE, not the HTTP status, because storage has answered a
 *            duplicate as HTTP 400 with "409" only in the body.
 *   network  fetch threw, a timeout, HTTP 5xx or 429. Retried on 2, 5, 15, 30
 *            then every 30 seconds. The bar does not change.
 *   refusal  anything else. The server said no, so the next piece would be
 *            refused too: the state goes to 'not_recorded' at once, the queue
 *            is emptied and nothing more is sent for this seat.
 *
 * A 401 first gets one session refresh and one retry; only a second refusal
 * counts.
 */

export const QUEUE_MAX_PIECES = 8;
export const PIECE_MAX_AGE_MS = 4 * 60_000;
export const RETRY_STEPS_MS = [2_000, 5_000, 15_000, 30_000] as const;
export const REQUEST_TIMEOUT_MS = 20_000;

export type RecordingState = 'recording' | 'paused' | 'not_recorded';

export interface QueuedPiece {
  seq: number;
  offsetMs: number;
  durationMs: number;
  blob: Blob;
  mime: BareAudioMime;
  /** When the piece finished recording (queue clock), for the 4 minute limit. */
  finishedAt: number;
}

/** What storage-js hands back: { error } with a StorageError, or a thrown error. */
export interface UploadAnswer {
  error: unknown;
}

export interface UploadQueueDeps {
  huddleId: string;
  participantId: string;
  upload: (path: string, blob: Blob, contentType: string) => Promise<UploadAnswer>;
  record: (chunk: ChunkRecord) => Promise<void>;
  /** Resolves true when a fresh session was obtained. */
  refreshSession: () => Promise<boolean>;
  now: () => number;
  sleep: (ms: number) => Promise<void>;
  onState: (state: RecordingState) => void;
  timeoutMs?: number;
}

export type AnswerKind = 'ok' | 'network' | 'auth' | 'refusal';

function field(error: unknown, key: string): unknown {
  return typeof error === 'object' && error !== null
    ? (error as Record<string, unknown>)[key]
    : undefined;
}

function statusOf(error: unknown): number {
  const status = field(error, 'status');
  if (typeof status === 'number') return status;
  const parsed = Number(status);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  const message = field(error, 'message');
  return typeof message === 'string' ? message : '';
}

export class TimeoutError extends Error {
  constructor() {
    super('huddle_upload_timeout');
    this.name = 'TimeoutError';
  }
}

/** jwt expired (gotrue style) or jose's "exp" claim check (storage server). */
const EXPIRED_SESSION = /jwt expired|"exp" claim timestamp check failed/i;

/** Sort a storage answer (plan 2.4). */
export function classifyUpload(error: unknown): AnswerKind {
  if (error === null || error === undefined) return 'ok';
  if (/already exists/i.test(messageOf(error))) return 'ok';
  if (error instanceof TimeoutError) return 'network';
  // StorageUnknownError: fetch itself threw, there is no HTTP answer at all.
  if (field(error, 'name') === 'StorageUnknownError') return 'network';
  if (error instanceof TypeError) return 'network';
  const status = statusOf(error);
  // Dev4 t_40b523ba: Storage sends most errors as HTTP 400 and keeps the real
  // code in the body (storage-js puts it in statusCode). An expired session
  // arrives as HTTP 400, body 403, with the JWT library's message; a busy or
  // read-only database as HTTP 400, body 503. Read both.
  const bodyStatus = Number(field(error, 'statusCode'));
  if (status === 401 || bodyStatus === 401) return 'auth';
  if (EXPIRED_SESSION.test(messageOf(error))) return 'auth';
  if (status >= 500 || status === 429) return 'network';
  if (bodyStatus >= 500 || bodyStatus === 429) return 'network';
  return 'refusal';
}

/** Sort an rpc answer. postgrest-js reports a thrown fetch as status 0. */
export function classifyRpc(error: unknown): AnswerKind {
  if (error === null || error === undefined) return 'ok';
  if (error instanceof TimeoutError) return 'network';
  if (error instanceof HuddleRpcError) {
    if (error.status === 0) return 'network';
    if (error.status === 401) return 'auth';
    if (error.status >= 500 || error.status === 429) return 'network';
    return 'refusal';
  }
  if (error instanceof TypeError) return 'network';
  return 'refusal';
}

export function retryDelay(attempt: number): number {
  return RETRY_STEPS_MS[Math.min(attempt, RETRY_STEPS_MS.length - 1)] ?? 30_000;
}

export class UploadQueue {
  private readonly deps: UploadQueueDeps;
  private readonly pieces: (QueuedPiece & { uploaded: boolean })[] = [];
  private running = false;
  private stopped = false;
  private state: RecordingState = 'recording';
  private idleWaiters: (() => void)[] = [];

  constructor(deps: UploadQueueDeps) {
    this.deps = deps;
  }

  get currentState(): RecordingState {
    return this.state;
  }

  get size(): number {
    return this.pieces.length;
  }

  /** Adds a finished piece. False when the seat is no longer being recorded. */
  push(piece: QueuedPiece): boolean {
    if (this.stopped || this.state === 'not_recorded') return false;
    this.pieces.push({ ...piece, uploaded: false });
    this.enforceLimits();
    void this.pump();
    return true;
  }

  /** Resolves when nothing is left to send (or the queue was stopped). */
  whenIdle(): Promise<void> {
    if (!this.running && this.pieces.length === 0) return Promise.resolve();
    return new Promise((resolve) => {
      this.idleWaiters.push(resolve);
    });
  }

  /** Drops everything and sends nothing more. */
  stop(): void {
    this.stopped = true;
    this.pieces.length = 0;
    this.settleIdle();
  }

  private setState(next: RecordingState): void {
    if (this.state === next || this.state === 'not_recorded') return;
    this.state = next;
    this.deps.onState(next);
  }

  private refuse(): void {
    this.pieces.length = 0;
    this.stopped = true;
    if (this.state !== 'not_recorded') {
      this.state = 'not_recorded';
      this.deps.onState('not_recorded');
    }
    this.settleIdle();
  }

  private settleIdle(): void {
    const waiters = this.idleWaiters;
    this.idleWaiters = [];
    for (const resolve of waiters) resolve();
  }

  /** Cap 8 pieces; a piece older than 4 minutes would be refused anyway (N2). */
  private enforceLimits(): void {
    const now = this.deps.now();
    let dropped = false;
    while (this.pieces.length > 0) {
      const head = this.pieces[0];
      if (head === undefined) break;
      const tooOld = now - head.finishedAt > PIECE_MAX_AGE_MS;
      const tooMany = this.pieces.length > QUEUE_MAX_PIECES;
      if (!tooOld && !tooMany) break;
      this.pieces.shift();
      dropped = true;
    }
    if (dropped) this.setState('paused');
  }

  private async withTimeout<T>(work: Promise<T>): Promise<T> {
    const ms = this.deps.timeoutMs ?? REQUEST_TIMEOUT_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        work,
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(() => reject(new TimeoutError()), ms);
        }),
      ]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  private async tryUpload(piece: QueuedPiece): Promise<AnswerKind> {
    const path = piecePath(this.deps.huddleId, this.deps.participantId, piece.seq, piece.mime);
    try {
      const answer = await this.withTimeout(this.deps.upload(path, piece.blob, piece.mime));
      return classifyUpload(answer.error);
    } catch (thrown) {
      return classifyUpload(thrown ?? new TypeError('upload failed'));
    }
  }

  private async tryRecord(piece: QueuedPiece): Promise<AnswerKind> {
    try {
      await this.withTimeout(
        this.deps.record({
          participantId: this.deps.participantId,
          seq: piece.seq,
          offsetMs: piece.offsetMs,
          durationMs: piece.durationMs,
          bytes: piece.blob.size,
          mime: piece.mime,
        }),
      );
      return 'ok';
    } catch (thrown) {
      return classifyRpc(thrown);
    }
  }

  /** One step with the 401 rule: refresh once, retry once. */
  private async attempt(step: () => Promise<AnswerKind>): Promise<AnswerKind> {
    const first = await step();
    if (first !== 'auth') return first;
    const refreshed = await this.deps.refreshSession().catch(() => false);
    if (!refreshed) return 'refusal';
    const second = await step();
    return second === 'auth' ? 'refusal' : second;
  }

  private async pump(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      let failures = 0;
      while (!this.stopped) {
        this.enforceLimits();
        const piece = this.pieces[0];
        if (piece === undefined) break;

        const kind = piece.uploaded
          ? await this.attempt(() => this.tryRecord(piece))
          : await this.attempt(() => this.tryUpload(piece));
        if (this.stopped) break;
        // Dropped by the age or size limit while it was in flight: its answer
        // no longer matters.
        if (!this.pieces.includes(piece)) {
          failures = 0;
          continue;
        }

        if (kind === 'refusal') {
          this.refuse();
          break;
        }
        if (kind === 'network') {
          await this.deps.sleep(retryDelay(failures));
          failures += 1;
          continue;
        }
        failures = 0;
        if (!piece.uploaded) {
          piece.uploaded = true;
          continue; // the row, for the same piece
        }
        this.pieces.splice(this.pieces.indexOf(piece), 1);
        if (this.state === 'paused') this.setState('recording');
      }
    } finally {
      this.running = false;
      if (this.pieces.length === 0 || this.stopped) this.settleIdle();
    }
  }
}
